import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useGetUserID } from '../hooks/useGetUserID';
import Walkthrough from '../components/Walkthrough';
import '../styles/cupboard.css';

const CUPBOARD_STEPS = [
    {
        target: '.add-from-photo-section .cupboard-dropdown-header',
        title: 'Open the Photo Section',
        content: 'Tap "Add from photo" to open the camera/photo area. The quickest way to stock your cupboard is a single snap of your fridge or pantry.',
        allowInteraction: true,
        waitForAction: true,
        interactionHint: 'Tap "Add from photo" above to open it'
    },
    {
        target: '.photo-upload-label',
        title: 'Take or Upload a Photo',
        content: 'Tap the button above to take a photo or pick one from your gallery. Point at your fridge, pantry or cupboard so the AI can see your ingredients.',
        allowInteraction: true,
        waitForAction: true,
        interactionHint: 'Tap "Add photo" — take a pic or choose from gallery'
    },
    {
        target: '.interpret-photo-btn',
        title: 'Let the AI Scan Your Photo',
        content: 'Great, your photo is ready! Tap "Interpret photo" and the AI will identify every ingredient it can see.',
        allowInteraction: true,
        waitForAction: true,
        interactionHint: 'Tap "Interpret photo" to start the scan'
    },
    {
        target: '.add-preview-to-cupboard-btn',
        title: "Here's What the AI Found!",
        content: 'Review the matched ingredients. You can tap any name to correct it — your fix is saved for next time. When happy, tap "Add to cupboard".',
        allowInteraction: true,
        waitForAction: true,
        interactionHint: 'Tap "Add to cupboard" when you\'re ready',
        tooltipPlacement: 'above'
    },
    {
        target: null,
        title: 'Your Cupboard is Ready!',
        content: 'Great — your ingredients are saved! Now let\'s find the best recipes you can make with what you have.',
        allowInteraction: false
    },
    {
        target: '.search-recipes-smart-btn',
        title: 'Search Recipes (Smart Order)',
        content: 'Tap this button to find recipes ranked by how well they match your cupboard. The top result needs the fewest extra ingredients!',
        allowInteraction: true,
        waitForAction: true,
        interactionHint: 'Tap "Search recipes (smart order)" to see your matches',
        tooltipPlacement: 'above',
        navigateTo: '/searchrecipes',
        navigationState: { fromCupboardSmart: true }
    }
];

export default function Cupboard() {
    const [cookies] = useCookies(['access_token']);
    const userID = useGetUserID();

    const navigate = useNavigate();

    // Token validation state
    const [tokenValidated, setTokenValidated] = useState(false);
    const [tokenValidationAttempted, setTokenValidationAttempted] = useState(false);

    // Helper to get auth token from cookies or localStorage
    const getAuthToken = () => cookies.access_token || window.localStorage.getItem('auth_token');

    // Helper to build Authorization header only when token exists
    const getAuthHeaders = () => {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };
    
    // Main state
    const [cupboardItems, setCupboardItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Add ingredients state (multiple at once)
    const [ingredientInputs, setIngredientInputs] = useState([
        { ingredientName: '', amount: '', unit: 'g', location: 'cupboard' }
    ]);
    
    // Autocomplete state
    const [suggestions, setSuggestions] = useState([]);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState({});
    const [showSuggestions, setShowSuggestions] = useState({});

    // Photo interpretation state (preview first, then add to cupboard)
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [photoInterpreting, setPhotoInterpreting] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const [matchedIngredients, setMatchedIngredients] = useState([]);
    const [addingPreviewToCupboard, setAddingPreviewToCupboard] = useState(false);
    const [savingCorrectionFor, setSavingCorrectionFor] = useState(null);
    // Matched-ingredient autocomplete (like editrecipe): suggestions pop up as you type
    const [matchedSuggestions, setMatchedSuggestions] = useState({});
    const [showMatchedSuggestionsFor, setShowMatchedSuggestionsFor] = useState(null);
    const [activeMatchedSuggestionIndex, setActiveMatchedSuggestionIndex] = useState(-1);

    // Dropdown state for compact mobile: Add ingredients manually & Add from photo
    const [manualDropdownOpen, setManualDropdownOpen] = useState(false);
    const [photoDropdownOpen, setPhotoDropdownOpen] = useState(false);

    // Cupboard walkthrough state
    const [showWalkthrough, setShowWalkthrough] = useState(false);
    const [walkthroughStep, setWalkthroughStep] = useState(0);
    const showWalkthroughRef = useRef(false);
    useEffect(() => { showWalkthroughRef.current = showWalkthrough; }, [showWalkthrough]);

    

    // Token validation effect - similar to CreateRecipe.js
    useEffect(() => {
        const validateToken = () => {
            // Check both cookie and localStorage for a token
            const cookieToken = cookies.access_token;
            const localStorageToken = localStorage.getItem('auth_token');
            const hasToken = !!cookieToken || !!localStorageToken;

            console.log('Cupboard token validation check:', {
                inCookie: !!cookieToken,
                inLocalStorage: !!localStorageToken,
                hasToken
            });

            if (!hasToken) {
                console.log('No auth token available');
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                navigate('/auth');
                return;
            }

            // Get the token to validate (prefer cookie, fallback to localStorage)
            const token = cookieToken || localStorageToken;
            
            try {
                // Manually validate the token by decoding and checking expiration
                const parts = token.split('.');
                if (parts.length !== 3) {
                    throw new Error('Invalid token format');
                }
                
                // Decode the payload
                const payload = JSON.parse(atob(parts[1]));
                const now = Math.floor(Date.now() / 1000);
                
                // Check if token is expired
                if (payload.exp && payload.exp < now) {
                    console.log('Token has expired');
                    setTokenValidated(false);
                    setTokenValidationAttempted(true);
                    setError('Your session has expired. Please log in again.');
                    navigate('/auth');
                    return;
                }
                
                console.log('Cupboard token validated successfully');
                setTokenValidated(true);
                setTokenValidationAttempted(true);
                setError(''); // Clear any previous session errors
                
            } catch (tokenError) {
                console.error('Token validation failed:', tokenError);
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                setError('Authentication error. Please log in again.');
                navigate('/auth');
            }
        };

        // Run validation immediately
        validateToken();
        
    }, [cookies.access_token, navigate]);

    // Listen for auth token updates from login
    useEffect(() => {
        const handleAuthTokenUpdate = (event) => {
            console.log('Cupboard: Auth token updated, refreshing auth state');
            // Force re-validation of the new token
            setTokenValidated(false);
            setTokenValidationAttempted(false);
        };

        window.addEventListener('auth-token-updated', handleAuthTokenUpdate);
        
        return () => {
            window.removeEventListener('auth-token-updated', handleAuthTokenUpdate);
        };
    }, []);

    // Start walkthrough when navigated here from the searchrecipes guide
    useEffect(() => {
        if (!tokenValidated || !tokenValidationAttempted) return;
        try {
            const saved = localStorage.getItem('walkthroughState');
            if (saved) {
                const state = JSON.parse(saved);
                if (state?.active && state?.page === 'cupboard') {
                    setWalkthroughStep(0);
                    setPhotoDropdownOpen(true);
                    setTimeout(() => setShowWalkthrough(true), 600);
                }
            }
        } catch (_) {}
    }, [tokenValidated, tokenValidationAttempted]);

    // Auto-advance step 0 → 1 when photo dropdown opens
    useEffect(() => {
        if (!showWalkthrough || walkthroughStep !== 0) return;
        if (photoDropdownOpen) setTimeout(() => setWalkthroughStep(1), 400);
    }, [photoDropdownOpen, showWalkthrough, walkthroughStep]);

    // Auto-advance step 1 → 2 when photo is selected
    useEffect(() => {
        if (!showWalkthrough || walkthroughStep !== 1) return;
        if (photoFile) setTimeout(() => setWalkthroughStep(2), 400);
    }, [photoFile, showWalkthrough, walkthroughStep]);

    // Auto-advance step 2 → 3 when AI returns results
    useEffect(() => {
        if (!showWalkthrough || walkthroughStep !== 2) return;
        if (matchedIngredients.length > 0) setTimeout(() => setWalkthroughStep(3), 600);
    }, [matchedIngredients.length, showWalkthrough, walkthroughStep]);

    useEffect(() => {
        // Only fetch cupboard items if token is validated and user ID exists
        if (!tokenValidated || !tokenValidationAttempted) {
            return; // Wait for token validation
        }

        if (!userID) {
            navigate('/auth');
            return;
        }

        if (tokenValidated) {
            fetchCupboardItems();
        }
    }, [userID, tokenValidated, tokenValidationAttempted]);

    const fetchCupboardItems = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${API_BASE_URL}/cupboard/${userID}`, {
                headers: getAuthHeaders()
            });
            
            if (response.data.success) {
                setCupboardItems(response.data.items);
            }
        } catch (error) {
            console.error('Error fetching cupboard items:', error);
            setError('Failed to load cupboard items');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = async (index, field, value) => {
        const newInputs = [...ingredientInputs];
        newInputs[index][field] = value;
        setIngredientInputs(newInputs);
        
        // If changing ingredient name, search for suggestions
        if (field === 'ingredientName') {
            if (value.length >= 2) {
                try {
                    const response = await axios.get(`${API_BASE_URL}/cupboard/search/ingredients`, {
                        params: { q: value, limit: 10 }
                    });
                    
                    if (response.data.success) {
                        setSuggestions(response.data.ingredients);
                        setShowSuggestions(prev => ({ ...prev, [index]: true }));
                        setActiveSuggestionIndex(prev => ({ ...prev, [index]: -1 }));
                    }
                } catch (error) {
                    console.error('Error searching ingredients:', error);
                    setSuggestions([]);
                }
            } else {
                setSuggestions([]);
                setShowSuggestions(prev => ({ ...prev, [index]: false }));
            }
        }
    };
    
    const handleSuggestionClick = (index, suggestion) => {
        const newInputs = [...ingredientInputs];
        newInputs[index].ingredientName = suggestion.name;
        setIngredientInputs(newInputs);
        
        // Hide suggestions
        setShowSuggestions(prev => ({ ...prev, [index]: false }));
        setSuggestions([]);
    };
    
    const handleKeyDown = (index, e) => {
        const currentSuggestions = suggestions;
        const isShowingSuggestions = showSuggestions[index];
        
        if (!isShowingSuggestions || currentSuggestions.length === 0) return;
        
        const currentActive = activeSuggestionIndex[index] || -1;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const nextIndex = currentActive < currentSuggestions.length - 1 ? currentActive + 1 : 0;
            setActiveSuggestionIndex(prev => ({ ...prev, [index]: nextIndex }));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const nextIndex = currentActive > 0 ? currentActive - 1 : currentSuggestions.length - 1;
            setActiveSuggestionIndex(prev => ({ ...prev, [index]: nextIndex }));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (currentActive >= 0 && currentSuggestions[currentActive]) {
                handleSuggestionClick(index, currentSuggestions[currentActive]);
            }
        } else if (e.key === 'Escape') {
            setShowSuggestions(prev => ({ ...prev, [index]: false }));
        }
    };

    const addNewInput = () => {
        setIngredientInputs([...ingredientInputs, 
            { ingredientName: '', amount: '', unit: 'g', location: 'cupboard' }
        ]);
    };

    const removeInput = (index) => {
        if (ingredientInputs.length > 1) {
            const newInputs = ingredientInputs.filter((_, i) => i !== index);
            setIngredientInputs(newInputs);
        }
    };

    const handleBulkAdd = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        
        // Filter out empty ingredients and validate they exist in JSON
        const validIngredients = [];
        const invalidIngredients = [];
        
        for (const ing of ingredientInputs) {
            if (ing.ingredientName.trim() && ing.amount && parseFloat(ing.amount) > 0) {
                // Validate ingredient exists in JSON
                try {
                    const response = await axios.get(`${API_BASE_URL}/cupboard/search/ingredients`, {
                        params: { q: ing.ingredientName.trim(), limit: 100 }
                    });
                    
                    const exactMatch = response.data.ingredients?.find(
                        item => item.name.toLowerCase() === ing.ingredientName.trim().toLowerCase()
                    );
                    
                    if (exactMatch) {
                        validIngredients.push(ing);
                    } else {
                        invalidIngredients.push(ing.ingredientName.trim());
                    }
                } catch (error) {
                    console.error('Error validating ingredient:', error);
                    invalidIngredients.push(ing.ingredientName.trim());
                }
            }
        }
        
        if (invalidIngredients.length > 0) {
            setError(`These ingredients are not in our database: ${invalidIngredients.join(', ')}. Please select from the suggestions.`);
            return;
        }
        
        if (validIngredients.length === 0) {
            setError('Please add at least one valid ingredient with name and amount');
            return;
        }
        
        try {
            const response = await axios.post(`${API_BASE_URL}/cupboard/add-bulk`, {
                ingredients: validIngredients
            }, {
                headers: getAuthHeaders()
            });
            
            if (response.data.success) {
                setSuccess(`Successfully added ${response.data.results.length} ingredients!`);
                // Reset form
                setIngredientInputs([
                    { ingredientName: '', amount: '', unit: 'g', location: 'cupboard' }
                ]);
                // Refresh data
                fetchCupboardItems();
                
                if (response.data.errors && response.data.errors.length > 0) {
                    setError(`Some errors occurred: ${response.data.errors.join(', ')}`);
                }
            }
        } catch (error) {
            console.error('Error adding ingredients:', error);
            setError(error.response?.data?.message || 'Failed to add ingredients');
        }
    };

    const handleDeleteItem = async (itemId) => {
        try {
            const response = await axios.delete(`${API_BASE_URL}/cupboard/${itemId}`, {
                headers: getAuthHeaders()
            });
            
            if (response.data.success) {
                fetchCupboardItems();
                setSuccess('Ingredient removed successfully');
            }
        } catch (error) {
            console.error('Error deleting ingredient:', error);
            setError('Failed to remove ingredient');
        }
    };

    const handleClearAllItems = async () => {
        if (!window.confirm('Are you sure you want to clear all items from your cupboard? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await axios.delete(`${API_BASE_URL}/cupboard/${userID}/clear-all`, {
                headers: getAuthHeaders()
            });
            
            if (response.data.success) {
                fetchCupboardItems();
                setSuccess(response.data.message);
            }
        } catch (error) {
            console.error('Error clearing cupboard:', error);
            setError('Failed to clear cupboard items');
        }
    };

    const ALLOWED_PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

    const handlePhotoSelect = (e) => {
        setPhotoError('');
        setMatchedIngredients([]);
        const file = e.target.files?.[0];
        if (!file) {
            setPhotoFile(null);
            setPhotoPreview(null);
            return;
        }
        const mime = (file.type || '').toLowerCase();
        if (!ALLOWED_PHOTO_TYPES.includes(mime)) {
            setPhotoError('Please choose a PNG, JPEG, or JPG image.');
            setPhotoFile(null);
            setPhotoPreview(null);
            e.target.value = '';
            return;
        }
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onload = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const clearPhoto = () => {
        setPhotoFile(null);
        setPhotoPreview(null);
        setPhotoError('');
        setMatchedIngredients([]);
    };

    const getMimeType = (file) => {
        const t = (file?.type || '').toLowerCase();
        return t === 'image/jpg' ? 'image/jpeg' : t || 'image/jpeg';
    };

    const handleInterpretPhoto = async () => {
        if (!photoFile) {
            setPhotoError('Please select a photo first.');
            return;
        }
        setPhotoInterpreting(true);
        setPhotoError('');
        setMatchedIngredients([]);
        try {
            const reader = new FileReader();
            const base64 = await new Promise((resolve, reject) => {
                reader.onload = () => {
                    const result = reader.result;
                    if (typeof result === 'string' && result.includes(',')) {
                        resolve(result.split(',')[1]);
                    } else {
                        resolve(result);
                    }
                };
                reader.onerror = reject;
                reader.readAsDataURL(photoFile);
            });
            const mimeType = getMimeType(photoFile);
            const response = await axios.post(
                `${API_BASE_URL}/cupboard/interpret-photo`,
                { image: base64, mimeType, addToCupboard: false },
                { headers: getAuthHeaders() }
            );
            if (response.data.success) {
                setMatchedIngredients(response.data.matchedIngredients || []);
                if ((response.data.matchedIngredients || []).length > 0) {
                    setSuccess(`Found ${response.data.matchedIngredients.length} ingredient(s). Review and add to cupboard below.`);
                } else {
                    setSuccess('Photo interpreted. No ingredients detected in the photo.');
                }
            }
        } catch (err) {
            console.error('Error interpreting photo:', err);
            setPhotoError(err.response?.data?.message || err.message || 'Failed to interpret photo.');
        } finally {
            setPhotoInterpreting(false);
        }
    };

    const removeIngredientFromPreview = (index) => {
        setMatchedIngredients((prev) => prev.filter((_, i) => i !== index));
    };

    const handleMatchedInputFocus = (index, e) => {
        // Move cursor to end of text when clicking to edit
        const input = e.target;
        setTimeout(() => {
            if (input && input.setSelectionRange) {
                const length = input.value.length;
                input.setSelectionRange(length, length);
            }
        }, 0);
        // Show suggestions if applicable
        const ing = matchedIngredients[index];
        const currentMatch = ing?.matched_ingredient || ing?.matchedName || '';
        const suggestionsList = matchedSuggestions[index] || [];
        if (currentMatch.length >= 2 && suggestionsList.length > 0) {
            setShowMatchedSuggestionsFor(index);
        }
    };

    const handleMatchedInputChange = (index, value) => {
        setMatchedIngredients((prev) => {
            const next = [...prev];
            const row = { ...next[index], matchedName: value, matched_ingredient: value };
            next[index] = row;
            return next;
        });
        if (value.length >= 2) {
            axios.get(`${API_BASE_URL}/cupboard/search/ingredients`, {
                params: { q: value, limit: 10 },
                headers: getAuthHeaders()
            })
                .then((res) => {
                    if (res.data?.success && res.data?.ingredients) {
                        setMatchedSuggestions((s) => ({ ...s, [index]: res.data.ingredients }));
                        setShowMatchedSuggestionsFor(index);
                        setActiveMatchedSuggestionIndex(-1);
                    }
                })
                .catch(() => {
                    setMatchedSuggestions((s) => ({ ...s, [index]: [] }));
                    setShowMatchedSuggestionsFor(index);
                });
        } else {
            setMatchedSuggestions((s) => ({ ...s, [index]: [] }));
            setShowMatchedSuggestionsFor(value.length > 0 ? index : null);
        }
    };

    const handleMatchedSuggestionSelect = async (index, suggestion) => {
        const name = suggestion?.name;
        if (!name) return;
        const ing = matchedIngredients[index];
        if (!ing) return;
        setMatchedIngredients((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], matchedName: name, matched_ingredient: name };
            return next;
        });
        setShowMatchedSuggestionsFor(null);
        setMatchedSuggestions((s) => ({ ...s, [index]: [] }));
        // Save correction even if unchanged - this confirms the match was correct for future interpretations
        const originalName = ing.originalName ?? ing.matched_ingredient;
        if (originalName) {
            setSavingCorrectionFor(index);
            try {
                await axios.post(
                    `${API_BASE_URL}/cupboard/interpretation-correction`,
                    { originalName, correctedIngredient: name },
                    { headers: getAuthHeaders() }
                );
            } catch (err) {
                console.error('Failed to save correction:', err);
                setError(err.response?.data?.message || 'Could not save correction.');
            } finally {
                setSavingCorrectionFor(null);
            }
        }
    };

    const handleMatchedKeyDown = (index, e) => {
        const list = matchedSuggestions[index] || [];
        if (list.length === 0 || showMatchedSuggestionsFor !== index) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveMatchedSuggestionIndex((i) => (i < list.length - 1 ? i + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveMatchedSuggestionIndex((i) => (i > 0 ? i - 1 : list.length - 1));
        } else if (e.key === 'Enter' && activeMatchedSuggestionIndex >= 0 && list[activeMatchedSuggestionIndex]) {
            e.preventDefault();
            handleMatchedSuggestionSelect(index, list[activeMatchedSuggestionIndex]);
        } else if (e.key === 'Escape') {
            setShowMatchedSuggestionsFor(null);
            setActiveMatchedSuggestionIndex(-1);
        }
    };

    const handleMatchedBlur = (index) => {
        setTimeout(() => {
            setShowMatchedSuggestionsFor(null);
            setActiveMatchedSuggestionIndex(-1);
        }, 200);
    };

    const handleAddPreviewToCupboard = async () => {
        if (!matchedIngredients.length) return;
        setAddingPreviewToCupboard(true);
        setError('');
        try {
            // Deduplicate: if multiple detections map to the same matched_ingredient, combine them
            // e.g. "pasta" → "pasta, penne" and "penne" → "pasta, penne" should become one entry
            const deduplicatedMap = new Map();
            for (const ing of matchedIngredients) {
                const key = (ing.matched_ingredient || ing.originalName || '').toLowerCase().trim();
                if (!key) continue;
                if (deduplicatedMap.has(key)) {
                    // Combine amounts for duplicate matched ingredients
                    const existing = deduplicatedMap.get(key);
                    existing.amount += parseFloat(ing.suggestedAmount ?? 100);
                    // Keep the first unit (or prefer 'g' if available)
                    if (!existing.unit || existing.unit === 'pieces') {
                        existing.unit = ing.suggestedUnit ?? 'g';
                    }
                } else {
                    deduplicatedMap.set(key, {
                        ingredientName: ing.matched_ingredient || ing.originalName,
                        amount: parseFloat(ing.suggestedAmount ?? 100),
                        unit: ing.suggestedUnit ?? 'g',
                        location: 'cupboard',
                        // Keep track of original names for corrections
                        originalNames: ing.originalName ? [ing.originalName] : []
                    });
                }
                // Track original names for corrections (even if matched_ingredient is same)
                if (ing.originalName && deduplicatedMap.has(key)) {
                    const entry = deduplicatedMap.get(key);
                    if (!entry.originalNames.includes(ing.originalName)) {
                        entry.originalNames.push(ing.originalName);
                    }
                }
            }
            const ingredients = Array.from(deduplicatedMap.values()).map(({ originalNames, ...ing }) => ing);
            const response = await axios.post(
                `${API_BASE_URL}/cupboard/add-bulk`,
                { ingredients },
                { headers: getAuthHeaders() }
            );
            if (response.data.success) {
                // Save corrections for all matched ingredients - this confirms the AI's matches were correct
                // so future interpretations can use these confirmed matches
                // Use deduplicated map to avoid saving duplicate corrections
                const correctionMap = new Map();
                for (const ing of matchedIngredients) {
                    if (ing.originalName && ing.matched_ingredient) {
                        const key = `${ing.originalName.toLowerCase().trim()}|${ing.matched_ingredient.toLowerCase().trim()}`;
                        if (!correctionMap.has(key)) {
                            correctionMap.set(key, { originalName: ing.originalName, correctedIngredient: ing.matched_ingredient });
                        }
                    }
                }
                const correctionPromises = Array.from(correctionMap.values()).map(({ originalName, correctedIngredient }) =>
                    axios.post(
                        `${API_BASE_URL}/cupboard/interpretation-correction`,
                        { originalName, correctedIngredient },
                        { headers: getAuthHeaders() }
                    ).catch((err) => {
                        console.warn('Failed to save correction for', originalName, err);
                        return null; // Don't fail the whole operation if one correction fails
                    })
                );
                await Promise.all(correctionPromises);
                setSuccess(`Added ${response.data.results?.length ?? ingredients.length} ingredient(s) to your cupboard.`);
                setMatchedIngredients([]);
                fetchCupboardItems();
                if (showWalkthroughRef.current) setWalkthroughStep(4);
            }
        } catch (err) {
            console.error('Error adding preview to cupboard:', err);
            setError(err.response?.data?.message || 'Failed to add ingredients to cupboard.');
        } finally {
            setAddingPreviewToCupboard(false);
        }
    };

    if (isLoading) {
        return (
            <div className="cupboard-container">
                <div className="loading">Loading your cupboard...</div>
            </div>
        );
    }

    return (
        <div className="cupboard-container">
            {/* <div className="cupboard-header">
                <h1>My Cupboard</h1>
                <p className="cupboard-description">Manage your ingredients and never waste food again</p>
            </div> */}

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Add Ingredients Form - dropdown for compact mobile */}
            <div className={`add-ingredients-section cupboard-dropdown ${manualDropdownOpen ? 'cupboard-dropdown-open' : ''}`}>
                <button
                    type="button"
                    className="cupboard-dropdown-header"
                    onClick={() => setManualDropdownOpen((o) => !o)}
                    aria-expanded={manualDropdownOpen}
                    aria-controls="cupboard-manual-content"
                    id="cupboard-manual-toggle"
                >
                    <span className="cupboard-dropdown-title">Add ingredients manually</span>
                    <span className="cupboard-dropdown-chevron" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                    </span>
                </button>
                <div id="cupboard-manual-content" className="cupboard-dropdown-body" role="region" aria-labelledby="cupboard-manual-toggle">
                <form onSubmit={handleBulkAdd}>
                    {ingredientInputs.map((ingredient, index) => (
                        <div key={index} className="ingredient-input-row">
                            <div className="ingredient-autocomplete-container">
                                <input
                                    type="text"
                                    placeholder="Start typing ingredient name..."
                                    value={ingredient.ingredientName}
                                    onChange={(e) => handleInputChange(index, 'ingredientName', e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onFocus={() => {
                                        if (ingredient.ingredientName.length >= 2 && suggestions.length > 0) {
                                            setShowSuggestions(prev => ({ ...prev, [index]: true }));
                                        }
                                    }}
                                    onBlur={() => {
                                        // Delay hiding to allow clicking on suggestions
                                        setTimeout(() => {
                                            setShowSuggestions(prev => ({ ...prev, [index]: false }));
                                        }, 200);
                                    }}
                                    className="ingredient-name-input"
                                    autoComplete="off"
                                />
                                {showSuggestions[index] && suggestions.length > 0 && (
                                    <div className="suggestions-dropdown">
                                        <div className="suggestions-header">
                                            {suggestions.length} ingredient{suggestions.length !== 1 ? 's' : ''} found
                                        </div>
                                        {suggestions.map((suggestion, suggestionIndex) => (
                                            <div
                                                key={suggestionIndex}
                                                className={`suggestion-item ${
                                                    activeSuggestionIndex[index] === suggestionIndex ? 'active' : ''
                                                }`}
                                                onClick={() => handleSuggestionClick(index, suggestion)}
                                                onMouseEnter={() => setActiveSuggestionIndex(prev => ({ ...prev, [index]: suggestionIndex }))}
                                            >
                                                {suggestion.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <input
                                type="number"
                                placeholder="Amount"
                                value={ingredient.amount}
                                onChange={(e) => handleInputChange(index, 'amount', e.target.value)}
                                className="amount-input"
                                min="0"
                                step="0.1"
                            />
                            <select
                                value={ingredient.unit}
                                onChange={(e) => handleInputChange(index, 'unit', e.target.value)}
                                className="unit-select"
                            >
                                <option value="g">g</option>
                                <option value="kg">kg</option>
                                <option value="ml">ml</option>
                                <option value="l">l</option>
                                <option value="pieces">pieces</option>
                                <option value="cups">cups</option>
                                <option value="tbsp">tbsp</option>
                                <option value="tsp">tsp</option>
                            </select>
                            <select
                                value={ingredient.location}
                                onChange={(e) => handleInputChange(index, 'location', e.target.value)}
                                className="location-select"
                            >
                                <option value="cupboard">Cupboard</option>
                                <option value="fridge">Fridge</option>
                                <option value="freezer">Freezer</option>
                                <option value="pantry">Pantry</option>
                            </select>
                            {ingredientInputs.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeInput(index)}
                                    className="remove-btn"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    ))}
                    
                    <div className="form-actions">
                        <button type="button" onClick={addNewInput} className="add-another-btn">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add Another Ingredient
                        </button>
                        <button type="submit" className="save-all-btn">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Save All Ingredients
                        </button>
                    </div>
                </form>
                </div>
            </div>

            {/* Add from photo - dropdown for compact mobile */}
            <div className={`add-from-photo-section cupboard-dropdown ${photoDropdownOpen ? 'cupboard-dropdown-open' : ''}`}>
                <button
                    type="button"
                    className="cupboard-dropdown-header"
                    onClick={() => setPhotoDropdownOpen((o) => !o)}
                    aria-expanded={photoDropdownOpen}
                    aria-controls="cupboard-photo-content"
                    id="cupboard-photo-toggle"
                >
                    <span className="cupboard-dropdown-title">Add from photo</span>
                    <span className="cupboard-dropdown-chevron" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                    </span>
                </button>
                <div id="cupboard-photo-content" className="cupboard-dropdown-body" role="region" aria-labelledby="cupboard-photo-toggle">
                <p className="photo-section-desc">Take or upload a photo of your cupboard, pantry or fridge. We&apos;ll match what we see to ingredients and add them for you.</p>
                <div className="photo-upload-area">
                    <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        capture="environment"
                        onChange={handlePhotoSelect}
                        className="photo-file-input"
                        id="cupboard-photo-input"
                    />
                    <label htmlFor="cupboard-photo-input" className="photo-upload-label">
                        <span className="photo-upload-icon" aria-hidden="true">
                            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth={2} />
                                <circle cx="8.5" cy="8.5" r="1.5" strokeWidth={2} />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15l-5-5L5 21" />
                            </svg>
                        </span>
                        Add photo
                    </label>
                </div>
                {photoPreview && (
                    <div className="photo-preview-wrap">
                        <img src={photoPreview} alt="Preview" className="photo-preview" />
                        <button type="button" onClick={clearPhoto} className="photo-clear-btn" title="Remove photo">✕</button>
                    </div>
                )}
                {photoError && <div className="error-message photo-error">{photoError}</div>}
                <div className="photo-actions">
                    <button
                        type="button"
                        onClick={handleInterpretPhoto}
                        disabled={!photoFile || photoInterpreting}
                        className="interpret-photo-btn"
                    >
                        {photoInterpreting ? (
                            <>Interpreting…</>
                        ) : (
                            <>
                                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                                Interpret photo
                            </>
                        )}
                    </button>
                </div>
                {matchedIngredients.length > 0 && (
                    <div className="matched-ingredients-preview">
                        <h3>Preview — ingredients from photo ({matchedIngredients.length})</h3>
                        <p className="preview-units-note">Original = what the AI saw; Matched = best match from our list. You can change the matched name — your choice is saved so future photos won’t repeat the same mistake. Units: g, ml, kg, or l; eggs as whole.</p>
                        <div className="preview-table-wrap">
                            <table className="preview-table">
                                <thead>
                                    <tr>
                                        <th>Original (AI)</th>
                                        <th>Matched</th>
                                        <th className="preview-remove-col"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {matchedIngredients.map((ing, idx) => {
                                        const currentMatch = ing.matched_ingredient || ing.matchedName || '';
                                        const suggestionsList = matchedSuggestions[idx] || [];
                                        const showSuggestions = showMatchedSuggestionsFor === idx && suggestionsList.length > 0;
                                        return (
                                            <tr key={idx}>
                                                <td className="preview-original">{ing.originalName ?? ing.matched_ingredient}</td>
                                                <td className={`preview-matched ${!currentMatch ? 'preview-no-match' : ''}`}>
                                                    <div className="preview-matched-autocomplete">
                                                        <input
                                                            type="text"
                                                            value={currentMatch}
                                                            onChange={(e) => handleMatchedInputChange(idx, e.target.value)}
                                                            onKeyDown={(e) => handleMatchedKeyDown(idx, e)}
                                                            onFocus={(e) => handleMatchedInputFocus(idx, e)}
                                                            onBlur={() => handleMatchedBlur(idx)}
                                                            disabled={savingCorrectionFor === idx}
                                                            className="preview-matched-input"
                                                            placeholder="Type to search..."
                                                            title="Change matched ingredient; pick a suggestion to save for future photos"
                                                            aria-label="Matched ingredient"
                                                            autoComplete="off"
                                                        />
                                                        {showSuggestions && (
                                                            <div
                                                                className="preview-matched-suggestions"
                                                                role="listbox"
                                                                aria-label="Ingredient suggestions"
                                                            >
                                                                <div className="preview-matched-suggestions-header">
                                                                    {suggestionsList.length} match{suggestionsList.length !== 1 ? 'es' : ''} — click or press Enter
                                                                </div>
                                                                {suggestionsList.map((suggestion, suggestionIndex) => (
                                                                    <div
                                                                        key={suggestionIndex}
                                                                        role="option"
                                                                        aria-selected={activeMatchedSuggestionIndex === suggestionIndex}
                                                                        className={`preview-matched-suggestion-item ${activeMatchedSuggestionIndex === suggestionIndex ? 'active' : ''}`}
                                                                        onMouseDown={(e) => {
                                                                            e.preventDefault();
                                                                            handleMatchedSuggestionSelect(idx, suggestion);
                                                                        }}
                                                                        onMouseEnter={() => setActiveMatchedSuggestionIndex(suggestionIndex)}
                                                                    >
                                                                        {suggestion.name}
                                                                        {suggestionIndex === 0 && <span className="preview-matched-best"> (Best match)</span>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {savingCorrectionFor === idx && <span className="preview-saving-label">Saving…</span>}
                                                    </div>
                                                </td>
                                                <td className="preview-remove-col">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeIngredientFromPreview(idx)}
                                                        className="preview-remove-btn"
                                                        title="Remove from preview"
                                                        aria-label="Remove from preview"
                                                    >
                                                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <button
                            type="button"
                            onClick={handleAddPreviewToCupboard}
                            disabled={addingPreviewToCupboard}
                            className="add-preview-to-cupboard-btn"
                        >
                            {addingPreviewToCupboard ? 'Adding…' : 'Add to cupboard'}
                        </button>
                    </div>
                )}
                </div>
            </div>

            {/* Cupboard Items */}
            <div className="cupboard-items-section">
                <div className="cupboard-section-header">
                    <h2>Your Cupboard ({cupboardItems.length} {cupboardItems.length === 1 ? 'item' : 'items'})</h2>
                    {cupboardItems.length > 0 && (
                        <div className="cupboard-header-actions">
                            <button
                                type="button"
                                onClick={() => navigate('/searchrecipes', { state: { fromCupboardSmart: true } })}
                                className="search-recipes-smart-btn"
                                title="Search recipes that match your cupboard ingredients, sorted in smart order"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                Search recipes (smart order)
                            </button>
                            <button 
                                onClick={handleClearAllItems}
                                className="clear-all-btn"
                                title="Clear all items from cupboard"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Clear All
                            </button>
                        </div>
                    )}
                </div>
                
                {/* Recently added from shopping notice */}
                {(() => {
                    const recentShoppingItems = cupboardItems.filter(item => 
                        item.addedFromShopping && 
                        item.addedFromShoppingDate &&
                        (new Date() - new Date(item.addedFromShoppingDate)) < (24 * 60 * 60 * 1000) // Last 24 hours
                    );
                    
                    return recentShoppingItems.length > 0 && (
                        <div className="recent-shopping-notice">
                            <p>
                                <span className="notice-icon">🛒</span>
                                {recentShoppingItems.length} ingredient{recentShoppingItems.length > 1 ? 's' : ''} recently added from shopping completion
                            </p>
                        </div>
                    );
                })()}
                
                {cupboardItems.length === 0 ? (
                    <div className="empty-cupboard">
                        <div className="empty-icon">
                            <svg className="w-16 h-16 mx-auto mb-4 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <h3>Your cupboard is empty</h3>
                        <p>Add some ingredients above to get started and never waste food again!</p>
                    </div>
                ) : (
                    <div className="cupboard-items-grid">
                        {cupboardItems.map((item) => {
                            // Check if item was recently added from shopping (within last 24 hours)
                            const isRecentFromShopping = item.addedFromShopping && 
                                item.addedFromShoppingDate &&
                                (new Date() - new Date(item.addedFromShoppingDate)) < (24 * 60 * 60 * 1000);
                            
                            return (
                                <div key={item._id} className={`cupboard-item ${isRecentFromShopping ? 'recent-from-shopping' : ''}`}>
                                    <div className="item-header">
                                        <h3 className="item-name">
                                            {item.ingredientName}
                                            {isRecentFromShopping && (
                                                <span className="shopping-indicator" title="Recently added from shopping">
                                                    🛒
                                                </span>
                                            )}
                                        </h3>
                                        <button
                                            onClick={() => handleDeleteItem(item._id)}
                                            className="delete-btn"
                                            title="Remove from cupboard"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="item-details">
                                        <p className="amount">{item.amount} {item.unit}</p>
                                        {isRecentFromShopping && (
                                            <p className="shopping-date">
                                                Added: {new Date(item.addedFromShoppingDate).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Walkthrough
                isOpen={showWalkthrough}
                steps={CUPBOARD_STEPS}
                currentStep={walkthroughStep}
                onNext={() => setWalkthroughStep(s => s + 1)}
                navigate={navigate}
                onComplete={() => {
                    setShowWalkthrough(false);
                    setWalkthroughStep(0);
                    localStorage.removeItem('walkthroughState');
                }}
                onSkip={() => {
                    setShowWalkthrough(false);
                    setWalkthroughStep(0);
                    localStorage.removeItem('walkthroughState');
                }}
                onClose={() => {
                    setShowWalkthrough(false);
                    setWalkthroughStep(0);
                    localStorage.removeItem('walkthroughState');
                }}
            />
        </div>
    );
} 